import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getQueryAnalytics, getSCGUsageAnalytics, getCostAnalytics } from '../services/analyticsApi';
import {
  ChartBarIcon,
  ClockIcon,
  UserIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const RANGE_OPTIONS = [
  { label: '24h', value: '24h' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: 'All', value: 'all' },
];

const TAB_OPTIONS = [
  { label: 'Query Stats', value: 'queries' },
  { label: 'SCG Usage', value: 'scg' },
  { label: 'LLM Costs', value: 'costs' },
];

const TYPE_COLORS = {
  classification: '#3b82f6',
  tearline: '#8b5cf6',
  foia: '#ef4444',
  scg: '#f59e0b',
  search: '#10b981',
  other: '#6b7280',
};

const LEVEL_COLORS = {
  UNCLASSIFIED: '#22c55e',
  CONFIDENTIAL: '#3b82f6',
  SECRET: '#f59e0b',
  'TOP SECRET': '#ef4444',
};

function SummaryCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="vault-card p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded flex items-center justify-center bg-[var(--vault-elevated)] border border-[var(--vault-border)]">
          <Icon className="w-5 h-5 text-[var(--spotlight-gold)]" />
        </div>
        <div>
          <div className="text-xs text-[var(--text-muted)] uppercase tracking-wide">{label}</div>
          <div className="text-xl font-bold text-[var(--text-primary)]">{value}</div>
          {sub && <div className="text-xs text-[var(--text-muted)]">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function BarChart({ data, colorMap, label, horizontal }) {
  if (!data || Object.keys(data).length === 0) {
    return <div className="text-sm text-[var(--text-muted)] py-4">No data available</div>;
  }
  const maxVal = Math.max(...Object.values(data), 1);

  if (horizontal) {
    return (
      <div className="space-y-2">
        {Object.entries(data).map(([key, val]) => (
          <div key={key} className="flex items-center gap-3">
            <div className="w-24 text-xs text-[var(--text-muted)] text-right truncate" title={key}>{key}</div>
            <div className="flex-1 h-5 bg-[var(--vault-elevated)] rounded overflow-hidden">
              <div
                className="h-full rounded transition-all duration-500"
                style={{
                  width: `${(val / maxVal) * 100}%`,
                  backgroundColor: (colorMap && colorMap[key]) || 'var(--spotlight-gold)',
                  minWidth: val > 0 ? '2px' : '0',
                }}
              />
            </div>
            <div className="w-12 text-xs text-[var(--text-secondary)] text-right">{val}</div>
          </div>
        ))}
      </div>
    );
  }

  // Vertical bars
  return (
    <div className="flex items-end gap-2" style={{ height: 160 }}>
      {Object.entries(data).map(([key, val]) => (
        <div key={key} className="flex-1 flex flex-col items-center gap-1">
          <div className="text-xs text-[var(--text-muted)]">{val}</div>
          <div
            className="w-full rounded-t transition-all duration-500"
            style={{
              height: `${Math.max((val / maxVal) * 120, 2)}px`,
              backgroundColor: (colorMap && colorMap[key]) || 'var(--spotlight-gold)',
            }}
          />
          <div className="text-[10px] text-[var(--text-muted)] truncate w-full text-center" title={key}>
            {key.length > 10 ? key.slice(0, 9) + '...' : key}
          </div>
        </div>
      ))}
    </div>
  );
}

function ConfidenceHistogram({ distribution }) {
  if (!distribution || distribution.length === 0) {
    return <div className="text-sm text-[var(--text-muted)] py-4">No data available</div>;
  }
  const maxVal = Math.max(...distribution.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-1" style={{ height: 140 }}>
      {distribution.map((bucket, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div className="text-[10px] text-[var(--text-muted)]">{bucket.count}</div>
          <div
            className="w-full rounded-t transition-all duration-500"
            style={{
              height: `${Math.max((bucket.count / maxVal) * 100, 2)}px`,
              backgroundColor: `hsl(${(bucket.range_start || i * 10) * 1.2}, 70%, 50%)`,
            }}
          />
          <div className="text-[9px] text-[var(--text-muted)]">
            {bucket.label || `${(bucket.range_start || i * 10)}-${(bucket.range_end || (i + 1) * 10)}`}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Query Stats Tab ---
function QueryStatsTab({ data }) {
  if (!data) return null;

  const avgTimeRaw = typeof data.avg_execution_time_ms === 'object'
    ? (data.avg_execution_time_ms?.classification || data.avg_execution_time_ms?.tearline || 0)
    : (data.avg_execution_time_ms || 0);
  const avgTime = avgTimeRaw
    ? `${(avgTimeRaw / 1000).toFixed(1)}s`
    : 'N/A';

  const topUser = data.top_users && data.top_users.length > 0
    ? data.top_users[0]
    : null;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard icon={ChartBarIcon} label="Total Queries" value={data.total_queries || 0} />
        <SummaryCard icon={ClockIcon} label="Avg Response Time" value={avgTime} />
        <SummaryCard
          icon={UserIcon}
          label="Top User"
          value={topUser ? topUser.username || topUser.user : 'N/A'}
          sub={topUser ? `${topUser.count} queries` : undefined}
        />
      </div>

      {/* Queries by type */}
      <div className="vault-card p-5">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-4">Queries by Type</h3>
        <BarChart data={data.queries_by_type} colorMap={TYPE_COLORS} />
      </div>

      {/* Daily trend */}
      {data.queries_over_time && data.queries_over_time.length > 0 && (
        <div className="vault-card p-5">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-4">Daily Query Trend</h3>
          <BarChart data={Object.fromEntries(data.queries_over_time.map(d => [d.date, d.count]))} horizontal />
        </div>
      )}

      {/* Confidence distribution */}
      {data.confidence_distribution && data.confidence_distribution.length > 0 && (
        <div className="vault-card p-5">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-4">Confidence Distribution</h3>
          <ConfidenceHistogram distribution={data.confidence_distribution} />
        </div>
      )}
    </div>
  );
}

// --- SCG Usage Tab ---
function SCGUsageTab({ data }) {
  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryCard icon={ShieldCheckIcon} label="Total Rule Firings" value={data.total_firings || 0} />
        <SummaryCard icon={CpuChipIcon} label="Unique Rules Fired" value={data.unique_rules_fired || 0} />
      </div>

      {/* Firings by level */}
      {data.firings_by_level && Object.keys(data.firings_by_level).length > 0 && (
        <div className="vault-card p-5">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-4">Firings by Classification Level</h3>
          <BarChart data={data.firings_by_level} colorMap={LEVEL_COLORS} />
        </div>
      )}

      {/* Top rules table */}
      {data.top_rules && data.top_rules.length > 0 && (
        <div className="vault-card p-5">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-4">Top 20 Rules</h3>
          <div className="space-y-1.5">
            {data.top_rules.slice(0, 20).map((rule, i) => {
              const maxCount = data.top_rules[0]?.count || 1;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 text-xs text-[var(--text-muted)] text-right">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-[var(--text-secondary)] truncate max-w-[300px]" title={rule.rule_id || rule.rule}>
                        {rule.rule_id || rule.rule}
                      </div>
                      {rule.level && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded"
                          style={{
                            backgroundColor: `${LEVEL_COLORS[rule.level] || '#6b7280'}22`,
                            color: LEVEL_COLORS[rule.level] || '#6b7280',
                          }}>
                          {rule.level}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 h-2 bg-[var(--vault-elevated)] rounded overflow-hidden">
                      <div
                        className="h-full rounded transition-all duration-500"
                        style={{
                          width: `${(rule.count / maxCount) * 100}%`,
                          backgroundColor: 'var(--spotlight-gold)',
                          minWidth: '2px',
                        }}
                      />
                    </div>
                  </div>
                  <div className="w-10 text-xs text-[var(--text-muted)] text-right">{rule.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// --- LLM Costs Tab ---
function CostsTab({ data }) {
  if (!data) return null;

  const totalCost = data.total_estimated_cost != null
    ? `$${data.total_estimated_cost.toFixed(4)}`
    : 'N/A';

  const totalTokens = data.total_tokens != null
    ? (typeof data.total_tokens === 'object'
      ? ((data.total_tokens.prompt || 0) + (data.total_tokens.completion || 0)).toLocaleString()
      : data.total_tokens.toLocaleString())
    : 'N/A';

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard icon={CurrencyDollarIcon} label="Total Cost" value={totalCost} />
        <SummaryCard icon={CpuChipIcon} label="Total Tokens" value={totalTokens} />
        <SummaryCard icon={ChartBarIcon} label="Total Calls" value={data.total_calls || 0} />
      </div>

      {/* Cost by service */}
      {data.cost_by_service && Object.keys(data.cost_by_service).length > 0 && (
        <div className="vault-card p-5">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-4">Cost by Service</h3>
          <BarChart data={data.cost_by_service} colorMap={TYPE_COLORS} />
        </div>
      )}

      {/* Cost by provider */}
      {data.cost_by_provider && Object.keys(data.cost_by_provider).length > 0 && (
        <div className="vault-card p-5">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-4">Cost by Provider</h3>
          <BarChart data={data.cost_by_provider} />
        </div>
      )}

      {/* Daily cost trend */}
      {data.cost_over_time && data.cost_over_time.length > 0 && (
        <div className="vault-card p-5">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-4">Daily Cost Trend</h3>
          <BarChart
            data={Object.fromEntries(data.cost_over_time.map(d => [d.date, parseFloat(d.cost.toFixed(4))]))}
            horizontal
          />
        </div>
      )}
    </div>
  );
}

// --- Main Analytics Page ---
export default function Analytics() {
  const [range, setRange] = useState('7d');
  const [activeTab, setActiveTab] = useState('queries');

  const { data: queryData, isLoading: queryLoading, error: queryError, refetch: refetchQueries } = useQuery({
    queryKey: ['analytics-queries', range],
    queryFn: () => getQueryAnalytics(range),
    staleTime: 2 * 60 * 1000,
  });

  const { data: scgData, isLoading: scgLoading, error: scgError, refetch: refetchSCG } = useQuery({
    queryKey: ['analytics-scg', range],
    queryFn: () => getSCGUsageAnalytics(range),
    staleTime: 2 * 60 * 1000,
  });

  const { data: costData, isLoading: costLoading, error: costError, refetch: refetchCosts } = useQuery({
    queryKey: ['analytics-costs', range],
    queryFn: () => getCostAnalytics(range),
    staleTime: 2 * 60 * 1000,
  });

  const isLoading = (activeTab === 'queries' && queryLoading) ||
                    (activeTab === 'scg' && scgLoading) ||
                    (activeTab === 'costs' && costLoading);

  const hasError = (activeTab === 'queries' && queryError) ||
                   (activeTab === 'scg' && scgError) ||
                   (activeTab === 'costs' && costError);

  const handleRefresh = () => {
    refetchQueries();
    refetchSCG();
    refetchCosts();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)] tracking-tight">Analytics</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">Platform usage, SCG rule activity, and LLM cost tracking</p>
        </div>
        <button
          onClick={handleRefresh}
          className="vault-btn vault-btn-secondary flex items-center gap-2"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Range selector + Tabs */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Tabs */}
        <div className="flex gap-1 bg-[var(--vault-elevated)] rounded p-1 border border-[var(--vault-border)]">
          {TAB_OPTIONS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-wide rounded transition-colors ${
                activeTab === tab.value
                  ? 'bg-[var(--spotlight-glow)] text-[var(--spotlight-gold)] border border-[var(--spotlight-gold)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Range */}
        <div className="flex gap-1 bg-[var(--vault-elevated)] rounded p-1 border border-[var(--vault-border)]">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setRange(opt.value)}
              className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                range === opt.value
                  ? 'bg-[var(--spotlight-glow)] text-[var(--spotlight-gold)] border border-[var(--spotlight-gold)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="vault-card p-12 text-center">
          <ArrowPathIcon className="w-8 h-8 animate-spin mx-auto text-[var(--spotlight-gold)]" />
          <p className="text-sm text-[var(--text-muted)] mt-3">Loading analytics...</p>
        </div>
      )}

      {/* Error state */}
      {hasError && !isLoading && (
        <div className="vault-card p-8 text-center border-[var(--error)]">
          <p className="text-sm text-[var(--error)]">Failed to load analytics data. Check that the analytics endpoints are available.</p>
          <button onClick={handleRefresh} className="vault-btn vault-btn-secondary mt-3 text-xs">
            Retry
          </button>
        </div>
      )}

      {/* Tab content */}
      {!isLoading && !hasError && (
        <>
          {activeTab === 'queries' && <QueryStatsTab data={queryData} />}
          {activeTab === 'scg' && <SCGUsageTab data={scgData} />}
          {activeTab === 'costs' && <CostsTab data={costData} />}
        </>
      )}
    </div>
  );
}
